import {
  apiChatFilesConfirmCreate,
  apiChatFilesCreate,
  apiChatFilesDestroy,
  apiChatFilesDownloadRetrieve
} from 'src/api/squad/sdk.gen'
import type { ChatFile } from 'src/api/squad/types.gen'
import type {
  ChatAttachment,
  ChatAttachmentApi,
  ChatAttachmentConfirmation,
  ChatFileListItem,
  ResolveAttachmentUrl
} from 'src/components/chat'

const appAuth = (appToken: string) => ({
  headers: { 'Saas-App-Token': appToken }
})

export const toChatAttachment = (file: ChatFile): ChatAttachment => ({
  id: file.id,
  filename: file.filename,
  mimeType: file.mime_type,
  sizeBytes: file.size_bytes,
  source: file.source,
  url: file.url
})

export const toChatFileListItem = (file: ChatFile): ChatFileListItem => ({
  ...toChatAttachment(file),
  createdAt: file.created_at
})

export const toConfirmation = (file: ChatFile): ChatAttachmentConfirmation =>
  file.status === 'ready'
    ? { status: 'ready', attachment: toChatAttachment(file) }
    : { status: 'failed', reason: file.error_message }

/** Uploads for the workspace home chat, authorised by the workspace's Squad
 * app token (the same token `useSquadAppToken` resolves for the chat). */
export const squadChatFilesApi = (
  agentId: string,
  appToken: string
): ChatAttachmentApi => ({
  create: async (file) => {
    const { data } = await apiChatFilesCreate({
      ...appAuth(appToken),
      body: {
        agent_id: agentId,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size
      },
      throwOnError: true
    })

    return { id: data.id, uploadUrl: data.upload_url, mimeType: data.mime_type }
  },
  confirm: async (id) => {
    const { data } = await apiChatFilesConfirmCreate({
      ...appAuth(appToken),
      path: { file_id: id },
      throwOnError: true
    })

    return toConfirmation(data)
  },
  remove: async (id) => {
    await apiChatFilesDestroy({
      ...appAuth(appToken),
      path: { file_id: id },
      throwOnError: true
    })
  }
})

/** A fresh presigned link: the one on a message may have expired. */
export const resolveSquadFileUrl =
  (appToken: string): ResolveAttachmentUrl =>
  async (attachment) => {
    const { data } = await apiChatFilesDownloadRetrieve({
      ...appAuth(appToken),
      path: { file_id: attachment.id },
      throwOnError: true
    })

    return data.url
  }
